import React, { useEffect } from "react";
import { Button, Link, OneColumnStack, Section, Stack } from "@arp/react-ndbx";
import { Paper, Typography, Fade } from "@mui/material";
import useTheme from "@mui/material/styles/useTheme";
import Page from "../../components/Page";

import { PATH_ONLINE } from "../../routes/paths";

import { Application, URLS } from "../../config/const/enums";
import usePreQualificationPages from "../../hooks/usePreQualificationPages";
import { pushEvent, trackifyPageChange } from "@allianz/tracking";
import useSharedData from "../../hooks/useSharedData";

import * as Sentry from "@sentry/react";

//----------------------------------------------------------------

export default function AcceptAndProceed() {
  const { state } = usePreQualificationPages();
  const { currentPage } = { ...state };
  const { pdsUrl } = useSharedData();
  const theme = useTheme();

  useEffect(() => {
    Sentry.captureMessage("Accept and Proceed: " + Application.ONLINE, "info");
    trackifyPageChange(currentPage?.identifier?.toLowerCase());
    pushEvent("purchasePath", Application.ONLINE, true);
  }, []);

  return (
    <>
      <Page title={currentPage.title} identifier={currentPage.identifier}>
        <Fade in={true} timeout={500}>
          <div>
            <Section mb={{ xs: 3, sm: 8 }} mt={{ xs: 3, md: 5, lg: 6 }}>
              <Section mb={{ xs: 2, md: 6 }}>
                <Typography variant="h2-bold">
                  Allianz Life Plan Quote
                </Typography>
                <Section md={10} sx={{ outline: "0px solid red" }}>
                  <Typography variant="p3">
                    Already saved an application?
                  </Typography>
                  <Link href={PATH_ONLINE.retrieveQuote}>
                    Retrieve your saved application.
                  </Link>
                </Section>
              </Section>
              <Paper
                elevation={0}
                md={12}
                sx={{
                  backgroundColor: theme.palette.soft.softBeige150,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  maxWidth: { xs: 343, sm: 704, md: 960 },
                  pb: { xs: 5, md: 8 },
                  pt: { xs: 3, md: 8 },
                  pl: { xs: 1, sm: 10, md: 3 },
                  pr: { xs: 1, sm: 10, md: 3 },
                }}
              >
                <Section xs={12}>
                  <Typography variant="p3">
                    Before you start your quote, read the following important
                    information
                  </Typography>
                </Section>

                <Section xs={12} mt={4} mb={2}>
                  <Typography variant="h5-bold">
                    Your duty to take reasonable care not to make a
                    misrepresentation (your Duty)
                  </Typography>
                </Section>
                <Section md={11} sx={{ lineHeight: 1.3 }}>
                  <Typography variant="p3">
                    You must take reasonable care not to make a
                    misrepresentation to us. This responsibility applies until
                    we issue you with a policy for the first time, or agree to
                    certain variations/changes, or agree to reinstate your
                    policy.
                  </Typography>
                </Section>
                <Section md={11} sx={{ lineHeight: 1.3 }}>
                  <Typography variant="p3">
                    You must answer our questions honestly, accurately and to
                    the best of your knowledge. The answers that you give us are
                    vital to our decision whether to insure you and on what
                    terms. A misrepresentation includes a statement that's
                    false, partially false, or which doesn’t fairly reflect the
                    truth. It's not misrepresentation if you don't answer a
                    question or if your answer is obviously incomplete or
                    irrelevant to the question asked.
                  </Typography>
                </Section>
                <Section md={11} sx={{ lineHeight: 1.3 }}>
                  <Typography variant="p3">
                    The responsibility to take reasonable care not to make a
                    misrepresentation applies to everyone who will be insured
                    under the policy. If you're answering questions on behalf of
                    anyone, we'll treat your answers or representations as
                    theirs.
                  </Typography>
                </Section>
                <Section md={11} sx={{ lineHeight: 1.3 }}>
                  <Typography variant="p3">
                    Whether or not you've taken reasonable care not to make a
                    misrepresentation is to be determined having regard to all
                    relevant circumstances, including the type of insurance, who
                    it's intended to be sold to, your particular characteristics
                    and circumstances we are aware of.
                  </Typography>
                </Section>
                <Section xs={12} mt={4} mb={2} sx={{ lineHeight: 1.3 }}>
                  <Typography variant="h6-bold">
                    If you don't meet your duty
                  </Typography>
                </Section>
                <Section md={11} sx={{ lineHeight: 1.3 }}>
                  <Typography variant="p3">
                    If you don't meet the above duty, we may reject or not fully
                    pay your claim.
                  </Typography>
                </Section>
                <Section md={11} sx={{ lineHeight: 1.3 }}>
                  <Typography variant="p3">
                    Within the first three years, we may treat your cover as if
                    it never existed if we wouldn't have insured you on any
                    terms if you had met the duty.
                  </Typography>
                </Section>
                <Section md={11} sx={{ lineHeight: 1.3 }}>
                  <Typography variant="p3">
                    Instead of treating your cover as if it never existed, we
                    may reduce the Cover Amount using a formula prescribed by
                    law (we can only do this within the first three years for
                    Life cover). For Critical Illness cover and Permanently
                    Unable to Work cover, we may also change the terms of your
                    cover to put us in the same position we would have been in
                    if you'd met the duty.
                  </Typography>
                </Section>
                <Section md={11} sx={{ lineHeight: 1.3 }}>
                  <Typography variant="p3">
                    If you deliberately provide dishonest or inaccurate answers
                    or were reckless in answering our questions, this is an act
                    of fraud, and we may treat your cover as if it never
                    existed.
                  </Typography>
                </Section>
                <Section md={11} sx={{ lineHeight: 1.3 }}>
                  <Typography variant="p3">
                    We'll apply these rights separately to Life cover, Critical
                    Illness cover and Permanently Unable to Work cover.
                  </Typography>
                </Section>
                <Section xs={12} mt={4} mb={2} sx={{ lineHeight: 1.3 }}>
                  <Typography variant="h6-bold">Contact Us</Typography>
                </Section>
                <Section md={11}>
                  <Typography variant="p3">
                    If you find our information or questions are unclear, you
                    can contact us on{" "}
                    <Link href={URLS.CONTACT_US_PHONE_URL}>
                      {URLS.CONTACT_US_PHONE_NUMBER}
                    </Link>{" "}
                    or
                    <Link href={URLS.MORE_INFO_URL} target="_blank">
                      visit our website for more information about this Duty
                    </Link>
                    .
                  </Typography>
                </Section>
              </Paper>
              <Section md={11} mt={{ xs: 3, md: 5 }}>
                <Typography variant="p3">By proceeding you agree</Typography>
              </Section>
              <Stack>
                <Typography textAlign={"left"} variant="p2">
                  <li>
                    that you have been provided with the{" "}
                    <Link href={pdsUrl} target="_blank">
                      Allianz Life Plan Product Disclosure Statement (PDS)
                    </Link>
                  </li>
                  <li>
                    to the terms of our{" "}
                    <Link
                      href={URLS.YOUR_DUTY_EXCLUSION_PRIVACY_NOTICE_URL}
                      target="_blank"
                    >
                      Privacy Notice
                    </Link>
                  </li>
                  <li>
                    to{" "}
                    <Link
                      href={URLS.YOUR_DUTY_EXCLUSION_PRIVACY_NOTICE_URL}
                      target="_blank"
                    >
                      your duty
                    </Link>{" "}
                    and understand understand that it's important you don't make
                    a misrepresentation
                  </li>
                </Typography>
              </Stack>
            </Section>
            <OneColumnStack
              flexShrink={1}
              sx={{
                width: { xs: 312, sm: 344, md: 312 },
              }}
            >
              <Button
                id="agree-proceed"
                ariaLabel="agree and proceed"
                variant="contained"
                color="primary"
                size="medium"
                type="submit"
              >
                I Agree and continue
              </Button>
            </OneColumnStack>{" "}
            <p data-type="version" style={{ display: "none" }}>
              Version: {import.meta.env.VITE_APP_VERSION}
            </p>
          </div>
        </Fade>
      </Page>
    </>
  );
}
